import { Activity, ChevronDown, Eye, TrendingUp } from "lucide-react";
import { RCA_MEASUREMENT_SECTION_LABELS } from "@/components/root-cause-analysis/rca-measurements-config-storage.js";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";

/** @typedef {import("@/components/root-cause-analysis/rca-measurements-config-storage.js").RcaMeasurementSectionId} RcaMeasurementSectionId */

const RCA_SECTION_SHELL =
  "rounded-lg border border-border bg-muted/60 px-3 py-3 sm:px-4";

/**
 * @param {RcaMeasurementSectionId} sectionId
 * @param {{ hoursPhrase: string }} props
 */
function MeasurementsLoadingSection({ sectionId, hoursPhrase }) {
  if (sectionId === "drift") {
    return (
      <section className={RCA_SECTION_SHELL}>
        <div className='flex w-full flex-wrap items-start gap-x-2 gap-y-2 sm:flex-nowrap sm:items-center'>
          <div className='flex min-w-0 flex-1 items-start gap-3'>
            <TrendingUp
              className='mt-0.5 h-5 w-5 shrink-0 text-foreground'
              aria-hidden
            />
            <div className='min-w-0 flex-1'>
              <h3 className='text-sm font-semibold text-foreground'>
                {RCA_MEASUREMENT_SECTION_LABELS.drift}
              </h3>
              <p className='mt-1 text-xs text-muted-foreground'>
                Magnitude and z-score vs. the last {hoursPhrase} before shutdown
                — loading…
              </p>
            </div>
          </div>
          <ChevronDown
            className='ml-auto h-9 w-9 shrink-0 text-muted-foreground sm:ml-0'
            aria-hidden
          />
        </div>
        <LoadingSkeleton className='mt-4 h-40 w-full min-w-0 rounded-md border border-border/60' />
      </section>
    );
  }
  if (sectionId === "keySensors") {
    return (
      <section className={RCA_SECTION_SHELL}>
        <div className='flex w-full flex-wrap items-start gap-x-2 gap-y-2 sm:flex-nowrap sm:items-center'>
          <div className='flex min-w-0 flex-1 items-start gap-2'>
            <Activity
              className='mt-0.5 h-5 w-5 shrink-0 text-muted-foreground'
              aria-hidden
            />
            <div className='min-w-0 flex-1'>
              <h3 className='text-sm font-semibold text-foreground'>
                Key Sensor Signals Before Shutdown
              </h3>
              <p className='mt-1 text-xs text-muted-foreground'>
                Loading Key Sensor Signals…
              </p>
            </div>
          </div>
          <ChevronDown
            className='ml-auto h-9 w-9 shrink-0 text-muted-foreground sm:ml-0'
            aria-hidden
          />
        </div>
        <LoadingSkeleton className='mt-4 h-52 w-full min-w-0 rounded-md border border-border/60' />
      </section>
    );
  }
  return (
    <section className={RCA_SECTION_SHELL}>
      <div className='flex w-full items-start gap-3'>
        <Eye className='mt-0.5 h-5 w-5 shrink-0 text-muted-foreground' aria-hidden />
        <div className='min-w-0 flex-1'>
          <h3 className='text-sm font-semibold text-foreground'>
            {RCA_MEASUREMENT_SECTION_LABELS.remaining}
          </h3>
          <p className='mt-1 text-xs text-muted-foreground'>
            Other sensors with plottable trends — loading…
          </p>
        </div>
        <ChevronDown
          className='ml-auto h-7 w-7 shrink-0 self-center text-muted-foreground'
          aria-hidden
        />
      </div>
      <LoadingSkeleton className='mt-4 h-52 w-full min-w-0 rounded-md border border-border/60' />
    </section>
  );
}

/**
 * Placeholders shown while measurement rows are loading: real section headers, full-width skeleton bodies.
 * @param {{
 *   hoursBack: number,
 *   sectionOrder: RcaMeasurementSectionId[],
 * }} props
 */
export default function MeasurementsSectionsLoading({ hoursBack, sectionOrder }) {
  const hoursPhrase = hoursBack === 1 ? "1 hour" : `${Number(hoursBack)} hours`;
  return (
    <div className='flex flex-col gap-3' aria-busy='true'>
      {sectionOrder.map((sectionId) => (
        <MeasurementsLoadingSection
          key={sectionId}
          sectionId={sectionId}
          hoursPhrase={hoursPhrase}
        />
      ))}
    </div>
  );
}
